import type { PurchaseOperatingMode } from './purchases.dto.js';
import {
  PURCHASES_SETTINGS_MANAGE_PERMISSION,
  PURCHASES_SETTLEMENT_CHECK_PERMISSION,
  PURCHASES_SETTLEMENT_OVERRIDE_PERMISSION,
  PURCHASES_SETTLEMENT_VOUCHER_PERMISSION,
} from '../permissions/permissions.dto.js';

export const purchaseSettlementMethodValues = [
  'cash',
  'check',
  'voucher',
  'electronic_transfer',
  'pending_payout',
  'mixed',
] as const;
export type PurchaseSettlementMethod = (typeof purchaseSettlementMethodValues)[number];

export const purchaseSettlementRuntimeMethodValues = ['cash', 'check', 'voucher'] as const;
export type PurchaseSettlementRuntimeMethod =
  (typeof purchaseSettlementRuntimeMethodValues)[number];

export const purchaseSettlementStatusValues = [
  'paid',
  'issued',
  'pending',
  'voided',
  'failed',
  'partially_settled',
] as const;
export type PurchaseSettlementStatus = (typeof purchaseSettlementStatusValues)[number];

export const purchaseSettlementPolicySourceValues = [
  'station',
  'location',
  'company',
  'system',
] as const;
export type PurchaseSettlementPolicySource =
  (typeof purchaseSettlementPolicySourceValues)[number];

export const purchaseSettlementReferenceModeValues = ['auto', 'manual'] as const;
export type PurchaseSettlementReferenceMode =
  (typeof purchaseSettlementReferenceModeValues)[number];

export const purchaseSettlementReferenceCounterScopeValues = [
  'company',
  'location',
  'station',
] as const;
export type PurchaseSettlementReferenceCounterScope =
  (typeof purchaseSettlementReferenceCounterScopeValues)[number];

export const purchaseSettlementReferenceCounterMethodValues = ['check', 'voucher'] as const;
export type PurchaseSettlementReferenceCounterMethod =
  (typeof purchaseSettlementReferenceCounterMethodValues)[number];

export interface PurchaseSettlementSelectionInput {
  method: PurchaseSettlementMethod;
  reference?: string | null;
  referenceMode?: PurchaseSettlementReferenceMode | null;
}

export interface PurchaseCheckSettlementMetadata {
  method: 'check';
  referenceLabel: string;
  referenceNumber: string;
}

export interface PurchaseVoucherSettlementMetadata {
  method: 'voucher';
  referenceLabel: string;
  referenceNumber: string;
  redeemableAt: string;
}

export type PurchaseSettlementMethodMetadata =
  | PurchaseCheckSettlementMetadata
  | PurchaseVoucherSettlementMetadata;

export interface PurchaseSettlementAllocation {
  id: string;
  method: PurchaseSettlementMethod;
  status: PurchaseSettlementStatus;
  amount: number;
  reference?: string | null;
  cashSessionId?: string | null;
  cashLocationId?: string | null;
  cashMovementId?: string | null;
  settledAt?: string | null;
  createdAt?: string | null;
}

export interface PurchaseSettlementSummary {
  requestedMethod: PurchaseSettlementMethod | null;
  resolvedMethod: PurchaseSettlementMethod;
  status: PurchaseSettlementStatus;
  totalAmount: number;
  settledAmount: number;
  pendingAmount: number;
  methodMetadata?: PurchaseSettlementMethodMetadata | null;
  allocations: PurchaseSettlementAllocation[];
}

export interface PurchaseSettlementPolicyOverride {
  scope: Extract<PurchaseSettlementPolicySource, 'location' | 'station'>;
  locationId?: string | null;
  stationId?: string | null;
  defaultMethod?: PurchaseSettlementRuntimeMethod | null;
  enabledMethods?: PurchaseSettlementRuntimeMethod[];
}

export interface PurchaseSettlementApprovalFlags {
  checkPayoutApproved: boolean;
  voucherPayoutApproved: boolean;
  checkDefaultApproved: boolean;
  voucherDefaultApproved: boolean;
  manualReferenceFastPayApproved: boolean;
  trainingModeApproved: boolean;
}

export interface PurchaseSettlementOneActionEligibility {
  cash: boolean;
  check: boolean;
  voucher: boolean;
}

export interface PurchaseSettlementManualReferenceAllowance {
  check: boolean;
  voucher: boolean;
  allowedAsOneActionDefault: boolean;
}

export interface PurchaseSettlementAutoReferenceMethodConfig {
  enabled: boolean;
  counterId?: string | null;
  prefix?: string | null;
  padding?: number | null;
}

export interface PurchaseSettlementAutoReferenceConfig {
  check: PurchaseSettlementAutoReferenceMethodConfig;
  voucher: PurchaseSettlementAutoReferenceMethodConfig;
}

export interface PurchaseSettlementAllowedOperatingModes {
  cash: PurchaseOperatingMode[];
  check: PurchaseOperatingMode[];
  voucher: PurchaseOperatingMode[];
}

export interface PurchaseSettlementRequiredPermissions {
  check: string[];
  voucher: string[];
  override: string[];
  policyAdmin: string[];
}

export interface PurchaseSettlementPolicyConfig {
  companyId?: string | null;
  companyDefaultMethod?: PurchaseSettlementRuntimeMethod | null;
  locationOverrides?: PurchaseSettlementPolicyOverride[];
  stationOverrides?: PurchaseSettlementPolicyOverride[];
  enabledMethods: PurchaseSettlementRuntimeMethod[];
  defaultMethod: PurchaseSettlementRuntimeMethod;
  source: PurchaseSettlementPolicySource;
  systemFallbackMethod: 'cash';
  approvalFlags: PurchaseSettlementApprovalFlags;
  oneActionEligibility: PurchaseSettlementOneActionEligibility;
  manualReferenceAllowance: PurchaseSettlementManualReferenceAllowance;
  autoReference: PurchaseSettlementAutoReferenceConfig;
  voucherRedeemableAt?: string | null;
  allowedOperatingModes: PurchaseSettlementAllowedOperatingModes;
  requiredPermissions: PurchaseSettlementRequiredPermissions;
}

export interface PurchaseSettlementMethodAvailability {
  method: PurchaseSettlementMethod;
  enabled: boolean;
  available: boolean;
  oneActionEligible: boolean;
  referenceModes?: PurchaseSettlementReferenceMode[];
  unavailableReason?: string | null;
  availableInModes?: PurchaseOperatingMode[];
}

export interface PurchaseSettlementAvailability {
  requestedMethod: PurchaseSettlementMethod | null;
  defaultMethod: PurchaseSettlementMethod;
  resolvedMethod: PurchaseSettlementMethod;
  enabledMethods: PurchaseSettlementMethod[];
  source: PurchaseSettlementPolicySource;
  methods: PurchaseSettlementMethodAvailability[];
}

export interface PurchaseSettlementReferenceCounter {
  id: string;
  companyId: string;
  scope: PurchaseSettlementReferenceCounterScope;
  locationId?: string | null;
  stationId?: string | null;
  method: PurchaseSettlementReferenceCounterMethod;
  prefix?: string | null;
  nextNumber: number;
  padding: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const defaultPurchaseSettlementPolicyConfig: PurchaseSettlementPolicyConfig = {
  enabledMethods: ['cash'],
  defaultMethod: 'cash',
  source: 'system',
  systemFallbackMethod: 'cash',
  approvalFlags: {
    checkPayoutApproved: false,
    voucherPayoutApproved: false,
    checkDefaultApproved: false,
    voucherDefaultApproved: false,
    manualReferenceFastPayApproved: false,
    trainingModeApproved: false,
  },
  oneActionEligibility: {
    cash: true,
    check: false,
    voucher: false,
  },
  manualReferenceAllowance: {
    check: false,
    voucher: false,
    allowedAsOneActionDefault: false,
  },
  autoReference: {
    check: { enabled: false },
    voucher: { enabled: false },
  },
  voucherRedeemableAt: null,
  allowedOperatingModes: {
    cash: ['production'],
    check: [],
    voucher: [],
  },
  requiredPermissions: {
    check: [PURCHASES_SETTLEMENT_CHECK_PERMISSION],
    voucher: [PURCHASES_SETTLEMENT_VOUCHER_PERMISSION],
    override: [PURCHASES_SETTLEMENT_OVERRIDE_PERMISSION],
    policyAdmin: [PURCHASES_SETTINGS_MANAGE_PERMISSION],
  },
};
